import { Emitter } from "./emitter";
import {
  CloseCode,
  NO_RECONNECT_CODES,
  PROTOCOL,
  type ErrorCode,
  type Hello,
  type JsonObject,
  type PushFrame,
  type Pushes,
  type PushType,
  type Requests,
  type RequestType,
} from "./protocol";

/** The subset of the browser WebSocket the client uses, so tests can inject a fake. */
export interface WebSocketLike {
  addEventListener(type: "message", listener: (event: { data: unknown }) => void): void;
  addEventListener(type: "close", listener: (event: { code: number; reason: string }) => void): void;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export type WebSocketConstructor = new (url: string, protocols: string | string[]) => WebSocketLike;

export interface GameSocketOptions {
  /** Endpoint, or a function read before every connection attempt (e.g. to add `?env=`). */
  url: string | (() => string);
  WebSocket?: WebSocketConstructor;
  /** Returns a number in [0, 1); used for backoff jitter. */
  random?: () => number;
  requestTimeoutMs?: number;
  helloTimeoutMs?: number;
  minBackoffMs?: number;
  maxBackoffMs?: number;
  /** Immediate reconnects for wrong_env (4307) in a row before they back off like failures. */
  maxRedirects?: number;
}

export type SocketStatus =
  | { kind: "idle" }
  | { kind: "connecting"; attempt: number }
  | { kind: "open"; hello: Hello; connection: number }
  | { kind: "waiting"; attempt: number; delayMs: number; maintenance: boolean; until?: number; code?: number }
  | { kind: "paused" }
  | { kind: "stopped"; code?: number; reason: string };

/** The server answered a request with ok:false. */
export class ServerError extends Error {
  constructor(
    readonly type: string,
    readonly code: ErrorCode,
    readonly details: JsonObject | undefined,
    message: string,
  ) {
    super(`${type}: ${code}: ${message}`);
    this.name = "ServerError";
  }
}

export type ConnectionErrorReason = "not_open" | "timeout" | "closed" | "too_large";

/** A request got no answer: not connected, timed out, or the connection closed. */
export class ConnectionError extends Error {
  constructor(
    readonly reason: ConnectionErrorReason,
    readonly type: string,
  ) {
    super(`${type}: ${reason}`);
    this.name = "ConnectionError";
  }
}

interface Pending {
  type: string;
  resolve: (payload: unknown) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

const encoder = new TextEncoder();

/**
 * One game.v1 connection with request/response correlation, application heartbeat and
 * reconnect with exponential backoff and full jitter.
 */
export class GameSocket {
  private readonly events = new Emitter<{ status: SocketStatus; push: PushFrame }>();
  private readonly pending = new Map<string, Pending>();
  private readonly WS: WebSocketConstructor;
  private readonly random: () => number;
  private readonly requestTimeoutMs: number;
  private readonly helloTimeoutMs: number;
  private readonly minBackoffMs: number;
  private readonly maxBackoffMs: number;
  private readonly maxRedirects: number;
  private ws: WebSocketLike | undefined;
  private hello: Hello | undefined;
  private current: SocketStatus = { kind: "idle" };
  private connection = 0;
  private attempt = 0;
  private redirects = 0;
  private nextId = 0;
  private maintenanceSeen = false;
  private maintenanceUntil: number | undefined;
  private connectTimer: ReturnType<typeof setTimeout> | undefined;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private idleTimer: ReturnType<typeof setTimeout> | undefined;
  private heartbeatTimer: ReturnType<typeof setInterval> | undefined;

  constructor(private readonly options: GameSocketOptions) {
    this.WS = options.WebSocket ?? (globalThis.WebSocket as unknown as WebSocketConstructor);
    this.random = options.random ?? Math.random;
    this.requestTimeoutMs = options.requestTimeoutMs ?? 10_000;
    this.helloTimeoutMs = options.helloTimeoutMs ?? 10_000;
    this.minBackoffMs = options.minBackoffMs ?? 500;
    this.maxBackoffMs = options.maxBackoffMs ?? 30_000;
    this.maxRedirects = options.maxRedirects ?? 3;
  }

  get status(): SocketStatus {
    return this.current;
  }

  /** The current connection's hello, or undefined while not connected. */
  get serverHello(): Hello | undefined {
    return this.hello;
  }

  onStatus(listener: (status: SocketStatus) => void): () => void {
    return this.events.on("status", listener);
  }

  onPush<K extends PushType>(type: K, listener: (payload: Pushes[K]) => void): () => void {
    return this.events.on("push", (frame) => {
      if (frame.type === type) listener(frame.payload as unknown as Pushes[K]);
    });
  }

  start(): void {
    if (this.ws || this.reconnectTimer) return;
    this.attempt = 0;
    this.redirects = 0;
    this.connect();
  }

  /** Closes for good; only start() connects again. */
  stop(reason = "stopped"): void {
    this.teardown(1000, reason);
    this.setStatus({ kind: "stopped", reason });
  }

  /** Closes cleanly without reconnecting, e.g. while the app is in the background. */
  pause(): void {
    const { kind } = this.current;
    if (kind === "idle" || kind === "stopped" || kind === "paused") return;
    this.teardown(1000, "paused");
    this.setStatus({ kind: "paused" });
  }

  resume(): void {
    if (this.current.kind !== "paused") return;
    this.attempt = 0;
    this.redirects = 0;
    if (this.maintenanceSeen && (this.maintenanceUntil ?? 0) > Date.now()) this.scheduleReconnect();
    else this.connect();
  }

  /** Drops the current connection and reconnects with backoff. */
  reconnect(reason = "reconnect"): void {
    const { kind } = this.current;
    if (kind === "idle" || kind === "stopped" || kind === "paused") return;
    this.teardown(1000, reason);
    this.scheduleReconnect();
  }

  /**
   * Drops the current connection and connects again right away, without counting a failure,
   * e.g. after wrong_env pinned another env. After `maxRedirects` in a row it backs off instead.
   */
  redirect(reason = "redirect"): void {
    const { kind } = this.current;
    if (kind === "idle" || kind === "stopped" || kind === "paused") return;
    this.teardown(1000, reason);
    this.scheduleRedirect();
  }

  request<K extends RequestType>(
    type: K,
    payload: Requests[K]["request"],
    timeoutMs = this.requestTimeoutMs,
  ): Promise<Requests[K]["response"]> {
    const ws = this.ws;
    if (!ws || !this.hello) return Promise.reject(new ConnectionError("not_open", type));
    const id = String(++this.nextId);
    const text = JSON.stringify({ id, type, payload });
    if (encoder.encode(text).length > this.hello.maxMessageBytes) {
      return Promise.reject(new ConnectionError("too_large", type));
    }
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new ConnectionError("timeout", type));
      }, timeoutMs);
      this.pending.set(id, { type, resolve: resolve as (payload: unknown) => void, reject, timer });
      try {
        ws.send(text);
      } catch {
        clearTimeout(timer);
        this.pending.delete(id);
        reject(new ConnectionError("closed", type));
      }
    });
  }

  private connect(): void {
    this.clearTimers();
    let ws: WebSocketLike;
    try {
      const { url } = this.options;
      ws = new this.WS(typeof url === "function" ? url() : url, PROTOCOL);
    } catch (err) {
      this.stop(`cannot connect: ${String(err)}`);
      return;
    }
    this.ws = ws;
    this.hello = undefined;
    // Events of a replaced socket are ignored by the identity checks, so listeners are never removed.
    ws.addEventListener("message", (event) => this.onMessage(ws, event.data));
    ws.addEventListener("close", (event) => this.onClose(ws, event.code, event.reason));
    this.connectTimer = setTimeout(() => this.reconnect("hello timeout"), this.helloTimeoutMs);
    this.setStatus({ kind: "connecting", attempt: this.attempt });
  }

  private onMessage(ws: WebSocketLike, data: unknown): void {
    if (ws !== this.ws) return;
    let frame: unknown;
    try {
      frame = typeof data === "string" ? JSON.parse(data) : undefined;
    } catch {
      frame = undefined;
    }
    if (!isObject(frame) || typeof frame.type !== "string") {
      this.malformed();
      return;
    }
    if (typeof frame.id === "string") this.onResponse(frame.id, frame);
    else if (isObject(frame.payload)) this.handlePush({ type: frame.type, payload: frame.payload });
    else this.malformed();
    if (this.ws === ws) this.armIdle();
  }

  private onResponse(id: string, frame: JsonObject): void {
    const pending = this.pending.get(id);
    if (!pending) return;
    this.pending.delete(id);
    clearTimeout(pending.timer);
    const type = String(frame.type);
    if (frame.ok === true) {
      pending.resolve(isObject(frame.payload) ? frame.payload : {});
      return;
    }
    const error = isObject(frame.error) ? frame.error : {};
    const code = (typeof error.code === "string" ? error.code : "internal") as ErrorCode;
    const details = isObject(error.details) ? error.details : undefined;
    if (code === "maintenance") {
      this.maintenanceSeen = true;
      this.maintenanceUntil = typeof details?.until === "number" ? details.until : undefined;
    }
    pending.reject(new ServerError(type, code, details, typeof error.message === "string" ? error.message : ""));
  }

  private handlePush(frame: PushFrame): void {
    if (frame.type === "hello") {
      if (this.hello) return;
      const hello = parseHello(frame.payload);
      if (!hello) {
        this.malformed();
        return;
      }
      this.onHello(hello);
    }
    this.events.emit("push", frame);
  }

  private onHello(hello: Hello): void {
    const ws = this.ws;
    clearTimeout(this.connectTimer);
    this.hello = hello;
    this.maintenanceSeen = false;
    this.maintenanceUntil = undefined;
    const timeoutMs = Math.min(hello.heartbeatMs, this.requestTimeoutMs);
    this.heartbeatTimer = setInterval(() => {
      this.request("ping", {}, timeoutMs).then(
        () => {
          this.attempt = 0;
          this.redirects = 0;
        },
        (err: unknown) => {
          if (this.ws === ws && err instanceof ConnectionError && err.reason === "timeout") {
            this.reconnect("ping timeout");
          }
        },
      );
    }, hello.heartbeatMs);
    this.setStatus({ kind: "open", hello, connection: ++this.connection });
  }

  private onClose(ws: WebSocketLike, code: number, reason: string): void {
    if (ws !== this.ws) return;
    this.teardown();
    if (NO_RECONNECT_CODES.includes(code)) this.setStatus({ kind: "stopped", code, reason });
    else if (code === CloseCode.wrongEnv) this.scheduleRedirect(code);
    else this.scheduleReconnect(code);
  }

  private malformed(): void {
    console.error("game socket: malformed frame");
    this.teardown(CloseCode.malformed, "malformed frame");
    this.scheduleReconnect();
  }

  private armIdle(): void {
    if (!this.hello) return;
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => this.reconnect("idle"), this.hello.heartbeatMs * 2);
  }

  private scheduleReconnect(code?: number): void {
    const maintenance = code === CloseCode.maintenance || this.maintenanceSeen;
    const attempt = this.attempt;
    let delayMs = this.backoffMs();
    const until = maintenance ? this.maintenanceUntil : undefined;
    const wait = (until ?? 0) - Date.now();
    // Jitter on top of the server's time, so clients don't all return at once.
    if (wait > 0) delayMs += wait;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      this.connect();
    }, delayMs);
    const status: SocketStatus = { kind: "waiting", attempt, delayMs, maintenance };
    if (until !== undefined) status.until = until;
    if (code !== undefined) status.code = code;
    this.setStatus(status);
  }

  /**
   * Reconnects on the next tick: not right away, so a wrong_env error that arrived with the close
   * is handled (and its env stored) before the next URL is built.
   */
  private scheduleRedirect(code?: number): void {
    if (this.redirects >= this.maxRedirects) {
      this.scheduleReconnect(code);
      return;
    }
    this.redirects++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      this.connect();
    }, 0);
    const status: SocketStatus = { kind: "waiting", attempt: this.attempt, delayMs: 0, maintenance: false };
    if (code !== undefined) status.code = code;
    this.setStatus(status);
  }

  /** Full jitter: uniform in [0, min(max, min * 2^attempt)). */
  private backoffMs(): number {
    const cap = Math.min(this.maxBackoffMs, this.minBackoffMs * 2 ** this.attempt);
    this.attempt = Math.min(this.attempt + 1, 32);
    return Math.floor(this.random() * cap);
  }

  private teardown(code?: number, reason?: string): void {
    const ws = this.ws;
    this.ws = undefined;
    this.hello = undefined;
    this.clearTimers();
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    if (ws && code !== undefined) {
      try {
        ws.close(code, reason);
      } catch {
        // Already closed.
      }
    }
    for (const pending of this.pending.values()) {
      clearTimeout(pending.timer);
      pending.reject(new ConnectionError("closed", pending.type));
    }
    this.pending.clear();
  }

  private clearTimers(): void {
    clearTimeout(this.connectTimer);
    clearTimeout(this.idleTimer);
    clearInterval(this.heartbeatTimer);
    this.connectTimer = this.idleTimer = this.heartbeatTimer = undefined;
  }

  private setStatus(status: SocketStatus): void {
    this.current = status;
    this.events.emit("status", status);
  }
}

function isObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseHello(payload: JsonObject): Hello | undefined {
  const { protocol, serverTime, heartbeatMs, authTimeoutMs, maxMessageBytes } = payload;
  if (protocol !== PROTOCOL) return undefined;
  if (![serverTime, heartbeatMs, authTimeoutMs, maxMessageBytes].every(Number.isSafeInteger)) return undefined;
  if ((heartbeatMs as number) < 1000 || (maxMessageBytes as number) < 1024) return undefined;
  return payload as unknown as Hello;
}
